/**
 * Shared, polled set of "lead UUIDs that have unread handoff alerts".
 *
 * Provider polls /v1/alerts/unread-lead-ids every 15 seconds; any
 * component can call `useUnreadLeadIds()` to get the live Set and stamp
 * a red dot pill next to those rows.
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { alertsService } from './services';

const UnreadLeadsCtx = createContext(new Set());

export function UnreadLeadsProvider({ children, pollMs = 15000 }) {
  const [ids, setIds] = useState(new Set());

  const refresh = useCallback(async () => {
    try {
      const arr = await alertsService.unreadLeadIds();
      setIds(new Set((Array.isArray(arr) ? arr : []).map(String)));
    } catch (_) { /* keep previous */ }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, pollMs);
    return () => clearInterval(t);
  }, [refresh, pollMs]);

  return (
    <UnreadLeadsCtx.Provider value={ids}>{children}</UnreadLeadsCtx.Provider>
  );
}

export function useUnreadLeadIds() {
  return useContext(UnreadLeadsCtx);
}

export function useHasUnreadForLead(leadId) {
  const set = useContext(UnreadLeadsCtx);
  if (!leadId) return false;
  return set.has(String(leadId));
}
